import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';

const DesignsList = () => {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    fetchDesigns();
  }, []);

  const fetchDesigns = async () => {
    try {
      const response = await api.get('/designs');
      setDesigns(response.data.designs);
    } catch (error) {
      console.error('Error fetching designs:', error);
      setError('Failed to load designs');
    } finally {
      setLoading(false);
    }
  };

  const deleteDesign = async (id) => {
    if (!window.confirm('Are you sure you want to delete this design?')) return;

    try {
      await api.delete(`/designs/${id}`);
      setDesigns(designs.filter(design => design._id !== id));
    } catch (error) {
      console.error('Error deleting design:', error);
      alert('Failed to delete design');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-lux-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-lux-bg py-16">
      <div className="max-w-7xl mx-auto px-8">
        <div className="flex justify-between items-end mb-12 border-b border-lux-border pb-8">
          <div>
            <p className="text-lux-gold uppercase tracking-[0.3em] text-[10px] font-bold mb-2">Portfolio</p>
            <h1 className="text-4xl font-display text-lux-primary">My Creative Essence</h1>
          </div>
          <Link
            to="/design-editor"
            className="bg-lux-primary text-white px-8 py-3 text-xs uppercase tracking-widest font-bold hover:bg-lux-gold transition-colors"
          >
            Create New Vision
          </Link>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-600 px-6 py-4 text-xs uppercase tracking-widest font-bold mb-8">
            {error}
          </div>
        )}

        {designs.length === 0 ? (
          <div className="text-center py-24 bg-white border border-lux-border rounded-sm shadow-sm font-sans flex flex-col items-center">
            <p className="text-lux-secondary text-sm mb-6 tracking-wide">You haven't manifested any designs yet.</p>
            <Link
              to="/design-editor"
              className="bg-lux-primary text-white px-8 py-3 text-xs uppercase tracking-widest font-bold hover:bg-lux-gold transition-colors"
            >
              Start Designing
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {designs.map((design) => (
              <div key={design._id} className="group bg-white border border-lux-border overflow-hidden hover:border-lux-gold transition-all duration-500 shadow-sm hover:shadow-xl hover:-translate-y-1">
                <div className="aspect-[4/3] bg-lux-bg flex items-center justify-center overflow-hidden border-b border-lux-border">
                  {design.thumbnail ? (
                    <img src={design.thumbnail} alt={design.name} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-700 p-4" />
                  ) : (
                    <span className="text-lux-secondary text-[10px] uppercase tracking-widest font-bold opacity-30">No Preview Available</span>
                  )}
                </div>
                <div className="p-8">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-display text-xl text-lux-primary">{design.name}</h3>
                    <div className="flex items-center gap-2">
                       {design.isPublic && (
                         <span className="bg-green-50 text-green-700 text-[8px] px-2 py-0.5 rounded tracking-tighter font-bold uppercase border border-green-100">Public</span>
                       )}
                    </div>
                  </div>
                  
                  {design.description && (
                    <p className="text-lux-secondary text-xs mb-6 italic font-sans leading-relaxed">{design.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between text-[9px] uppercase tracking-widest text-lux-secondary/60 mb-8 font-bold">
                    <span>Manifested: {new Date(design.createdAt).toLocaleDateString('en-IN')}</span>
                  </div>

                  <div className="flex gap-4">
                    <Link
                      to={`/design-editor?load=${design._id}`}
                      className="flex-1 bg-lux-bg text-lux-primary text-center py-3 text-[10px] uppercase font-bold tracking-widest border border-lux-border hover:border-lux-primary transition-all"
                    >
                      Refine
                    </Link>
                    <button
                      onClick={() => deleteDesign(design._id)}
                      className="px-6 py-3 bg-red-50 text-red-500 text-[10px] uppercase font-bold tracking-widest border border-red-100 hover:bg-red-500 hover:text-white transition-all"
                    >
                      Erase
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DesignsList;